
import { BreadCrumb } from 'primereact/breadcrumb';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppContext } from '../Context';
import { ApiEndpoints, Constants } from '../constants/appConstants';
import httpService from '../services/httpService';
import './ComponentHeader.css';

interface Breadcrumb {
    label: string;
    path?: string;
    url?: string;
}

interface ComponentHeaderProps {
    title?: string;
    breadcrumbs?: Breadcrumb[];
    showProviderTypeDropdown?: boolean;
    hideProviderTypeDropdown?: boolean;
    providerType?: string;
    providerTypeValue?: string;
    onProviderTypeChange?: (value: string) => void;
    onReviewYearChange?: (value: number | undefined) => void;
    reviewYear?: number;
}

type ErrorWithInnerException = Error & {
    innerException?: unknown;
    cause?: unknown;
};

type ReviewYearDetailsResponse = {
    reviewYear?: number;
};

const normalizeProviderType = (value?: string): string => {
    const normalized = String(value || '').trim().toUpperCase();

    if (normalized === Constants.SCOKey) {
        return Constants.SCOKey;
    }

    if (
        normalized === Constants.RSKey ||
        normalized === 'RESIDENTIAL' ||
        normalized === 'RH'
    ) {
        return Constants.RSKey;
    }

    return Constants.RSKey;
};

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return 'Unknown error';
};

const logError = (message: string, error: unknown): void => {
    const typedError = error as ErrorWithInnerException;

    console.error(message, {
        message: getErrorMessage(error),
        innerException: getErrorMessage(
            typedError?.innerException ?? typedError?.cause
        )
    });
};

const ComponentHeader: React.FC<ComponentHeaderProps> = ({
    title,
    breadcrumbs,
    showProviderTypeDropdown,
    hideProviderTypeDropdown = false,
    providerType: providerTypeProp,
    providerTypeValue,
    onProviderTypeChange,
    reviewYear: reviewYearProp,
    onReviewYearChange
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        reviewYear: contextReviewYear,
        providerType: contextProviderType,
        setProviderType,
        setReviewYear,
        providerTypes,
        isLoadingProviderTypes
    } = useAppContext();

    const selectedProviderType =
        providerTypeProp ??
        providerTypeValue ??
        contextProviderType;

    const hasProviderTypeOverride =
        providerTypeProp !== undefined ||
        providerTypeValue !== undefined;

    const displayReviewYear =
        hasProviderTypeOverride
            ? reviewYearProp
            : contextReviewYear;

    const shouldShowProviderTypeDropdown =
        !hideProviderTypeDropdown &&
        (showProviderTypeDropdown ?? true);

    const handleHomeNavigation = () => {
        try {
            const normalizedProviderType = normalizeProviderType(
                sessionStorage.getItem('selectedProviderType') ||
                    selectedProviderType
            );

            setProviderType(normalizedProviderType);

            navigate('/pages/home', {
                state: {
                    providerType: normalizedProviderType
                }
            });
        } catch (error: unknown) {
            logError(
                '[ComponentHeader] Error navigating home',
                error
            );
        }
    };

    const home = {
        icon: 'pi pi-home',
        command: handleHomeNavigation
    };

    const handleProviderTypeChange = (value: string) => {
        try {
            const normalizedValue =
                normalizeProviderType(value);

            if (typeof onProviderTypeChange === 'function') {
                onReviewYearChange?.(undefined);
                onProviderTypeChange(normalizedValue);
                return;
            }

            setProviderType(normalizedValue);

            if (location.pathname === '/dashboard') {
                navigate(
                    `/dashboard?providerType=${normalizedValue}`,
                    {
                        replace: false,
                        state: {
                            providerType: normalizedValue
                        }
                    }
                );
                return;
            }

            if (location.pathname === '/pfp/providers') {
                navigate(
                    `/pfp/providers?providerType=${normalizedValue}`,
                    {
                        replace: false,
                        state: {
                            providerType: normalizedValue
                        }
                    }
                );
            }
        } catch (error: unknown) {
            logError(
                '[ComponentHeader] Error changing provider type',
                error
            );
        }
    };

    useEffect(() => {
        let ignoreResponse = false;

        const fetchReviewYear = async (): Promise<void> => {
            if (!selectedProviderType) {
                return;
            }

            try {
                const response = await httpService.get<ReviewYearDetailsResponse>(
                    `${ApiEndpoints.GetCurrentReviewYearDetails}/${selectedProviderType}`
                );

                if (ignoreResponse) {
                    return;
                }

                const reviewYear = response?.data?.reviewYear;

                if (typeof onReviewYearChange === 'function') {
                    onReviewYearChange(reviewYear);
                    return;
                }

                if (typeof reviewYear === 'number') {
                    setReviewYear(reviewYear);
                }
            } catch (error: unknown) {
                if (!ignoreResponse) {
                    logError(
                        '[ComponentHeader] Error fetching review year',
                        error
                    );
                }
            }
        };

        void fetchReviewYear();

        return () => {
            ignoreResponse = true;
        };
    }, [
        selectedProviderType,
        onReviewYearChange,
        setReviewYear
    ]);

    const breadcrumbModel = breadcrumbs?.map(
        ({ label, path }) =>
            path
                ? {
                      label,
                      command: () => navigate(path)
                  }
                : {
                      label
                  }
    );

    const hasBreadcrumbs =
        Boolean(breadcrumbModel && breadcrumbModel.length > 0);

    return (
        <div className="component-header">
            <nav className="breadcrumbs">
                {hasBreadcrumbs && (
                    <BreadCrumb
                        model={breadcrumbModel}
                        home={home}
                        style={{
                            background: 'none',
                            border: 'none',
                            paddingLeft: '0px'
                        }}
                    />
                )}
            </nav>

            <div
                className={`component-header-controls ${
                    !hasBreadcrumbs ? 'no-breadcrumb' : ''
                }`}
            >
                {shouldShowProviderTypeDropdown && (
                    <div className="provider-type-control">
                        <span className="provider-type-label">
                            Provider Type:
                        </span>

                        <div className="provider-type-dropdown">
                            <Dropdown
                                value={selectedProviderType}
                                options={providerTypes}
                                optionLabel="providerType"
                                optionValue="providerCode"
                                onChange={(e) =>
                                    handleProviderTypeChange(e.value)
                                }
                                placeholder="Select Provider Type"
                                loading={isLoadingProviderTypes}
                            />
                        </div>
                    </div>
                )}

                <div
                    className="review-year-display"
                    aria-live="polite"
                >
                    <span>Review Year:</span>

                    {displayReviewYear ? (
                        <span>{displayReviewYear}</span>
                    ) : (
                        <Skeleton
                            width="3rem"
                            height="1rem"
                            aria-label="Loading review year"
                        />
                    )}
                </div>
            </div>

            {title && (
                <div className="component-header-title">
                    {title}
                </div>
            )}
        </div>
    );
};

export default ComponentHeader;
